/**
 * Shared HTTP error handling for the api layer.
 *
 * The backend's global exception filter serialises every error as
 *   { success: false, error: { statusCode, message, code? } }
 * so the human-readable text lives under `error.message`, NOT the top level.
 * Reading the top-level `message` (the old behaviour) always yielded `undefined`,
 * which collapsed every backend error to a generic "Request failed" string and
 * dropped the machine `code` entirely. These helpers read the nested envelope
 * first, then fall back to a top-level message (plain string/validation throws),
 * then a generic string — so the real reason always reaches the user.
 */

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    message: string,
    /** Stable machine code from the backend error envelope (e.g. DUPLICATE_SERVICE_SLOT), when present. */
    public readonly code?: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

function pickMessage(v: unknown): string | undefined {
  if (!v || typeof v !== 'object') return undefined;
  const m = (v as Record<string, unknown>).message;
  if (typeof m === 'string' && m.trim()) return m;
  if (Array.isArray(m)) {
    const joined = m.filter((x): x is string => typeof x === 'string').join(', ');
    return joined || undefined;
  }
  return undefined;
}

function pickCode(v: unknown): string | undefined {
  if (!v || typeof v !== 'object') return undefined;
  const c = (v as Record<string, unknown>).code;
  return typeof c === 'string' && c ? c : undefined;
}

/** Pull a human message out of a parsed backend JSON error body (nested envelope → top level). */
export function apiErrorMessage(data: unknown): string | undefined {
  if (!data || typeof data !== 'object') return undefined;
  const nested = (data as Record<string, unknown>).error;
  return pickMessage(nested) ?? pickMessage(data);
}

/** Pull the stable machine code out of a parsed backend JSON error body, if any. */
export function apiErrorCode(data: unknown): string | undefined {
  if (!data || typeof data !== 'object') return undefined;
  const nested = (data as Record<string, unknown>).error;
  return pickCode(nested) ?? pickCode(data);
}

/** Build an ApiError from an HTTP status + parsed JSON error body. */
export function parseApiError(status: number, data: unknown): ApiError {
  return new ApiError(status, apiErrorMessage(data) ?? 'Request failed', apiErrorCode(data));
}

/**
 * Localize an ApiError for display, keyed by its stable machine `code`
 * (`errors.<CODE>` in the `common` namespace), falling back to the backend's
 * human `message` when there is no code or no matching translation.
 *
 * This is the missing piece behind "error translation is missing": the backend
 * emits English strings (+ a code), so a coded error showed English even in
 * Arabic. With this, a translated code (e.g. SLOT_NOT_FEASIBLE) shows the
 * localized copy, while any un-translated / code-less error keeps the server
 * text exactly as before. Pass a `t` bound to the `common` namespace.
 */
export function apiErrorText(
  err: ApiError,
  t: (key: string, options?: { defaultValue?: string }) => string,
): string {
  if (!err.code) return err.message;
  return t(`errors.${err.code}`, { defaultValue: err.message });
}
